import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { useExerciseResultsForSetAutofill } from '../../../hooks/use-exercise-results-for-set-autofill';
import { useNotFound } from '../../../hooks/useNotFound';
import { useGetAvailableExercises } from '../../../redux/api/exercisesAPI';
import {
  useLazyGetProgramSetById,
  usePostPutProgramSet,
} from '../../../redux/api/programsAPI';
import { IExercise } from '../../../types/interfaces/exercise';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { orderBy } from '../../../utils/collections/array-utils';
import {
  findExerciseResultByExerciseId,
  mergeProgramResultIntoOptions,
} from '../../../utils/training/exercise-result-autofill';
import {
  copyProgramSetItemToNext,
  getEmptyProgramSetItem,
  orderExerciseOptions,
  orderProgramSet,
} from '../../../utils/training/set-utils';
import { getProgramValidation } from './FormikData';

const newProgramSetId = 'new';

export function useEditProgramSetComponent(setId?: string) {
  const [postPutProgramSet, { isLoading }] = usePostPutProgramSet();
  const [getProgramSet, { data: programSet, error }] =
    useLazyGetProgramSetById();
  const { back } = useNavigationStack();
  const { t } = useTranslation();
  const [showNewExerciseModal, setShowNewExerciseModal] = useState(false);

  const { data: availableExercises } = useGetAvailableExercises();
  const exercises = availableExercises || [];
  const { resolveRestForFirstItemAfterExerciseSelect } =
    useDefaultSetItemRestSec();
  const exerciseResults = useExerciseResultsForSetAutofill(undefined);

  useNotFound(!!error);

  useEffect(() => {
    if (setId && setId !== newProgramSetId) getProgramSet(setId);
  }, [setId]);

  const orderedProgramSet: IProgramSet = useMemo(() => {
    if (!programSet || !exercises) return null;

    const exercise = exercises.find((e) => e.id === programSet.exerciseId);

    return orderProgramSet(programSet, exercise);
  }, [programSet, exercises]);

  const formik = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    validationSchema: getProgramValidation(t),
    initialValues: {
      title: orderedProgramSet?.title || '',
      description: orderedProgramSet?.description || '',
      exercise: orderedProgramSet?.exercise || null,
      exerciseId: orderedProgramSet?.exerciseId || null,
      programSetItems: orderedProgramSet?.programSetItems || [],
      orderNumber: 0,
      setNumber: 0,
    },
    onSubmit,
  });

  function onSubmit(update) {
    const { exercise, ...body } = update;

    postPutProgramSet({ setId: programSet?.id, body: body })
      .unwrap()
      .then((response) => back())
      .catch((e) => console.log(e));
  }

  function onAddSetItemClick() {
    const maxOrderNumber =
      formik.values.programSetItems?.length > 0
        ? Math.max(
            ...formik.values.programSetItems.map((item) => item.orderNumber),
          )
        : -1;

    const newProgramSetItem =
      maxOrderNumber === -1
        ? (() => {
            const exerciseId = formik.values.exerciseId;
            const result = findExerciseResultByExerciseId(
              exerciseResults,
              exerciseId,
            );
            const rest = resolveRestForFirstItemAfterExerciseSelect(result);
            let item = getEmptyProgramSetItem(
              maxOrderNumber + 1,
              formik.values.exercise?.options,
              rest,
            );
            if (result) {
              item = {
                ...item,
                programSetItemOptions: mergeProgramResultIntoOptions(
                  item.programSetItemOptions,
                  result,
                ),
              };
            }
            return item;
          })()
        : copyProgramSetItemToNext(
            formik.values.programSetItems.find(
              (item) => item.orderNumber === maxOrderNumber,
            ),
          );

    formik.setFieldValue(
      'programSetItems',
      orderBy(
        [...formik.values.programSetItems, newProgramSetItem],
        'orderNumber',
      ),
    );
  }

  function onChangeExercise(exercise: IExercise) {
    if (exercise) {
      const orderedExercise = orderExerciseOptions(exercise);
      const result = findExerciseResultByExerciseId(
        exerciseResults,
        orderedExercise.id,
      );
      const rest = resolveRestForFirstItemAfterExerciseSelect(result);
      let firstItem = getEmptyProgramSetItem(
        0,
        exercises.find((e) => String(e.id) === String(orderedExercise.id))
          ?.options,
        rest,
      );
      if (result) {
        firstItem = {
          ...firstItem,
          programSetItemOptions: mergeProgramResultIntoOptions(
            firstItem.programSetItemOptions,
            result,
          ),
        };
      }
      formik.setFieldValue('exercise', orderedExercise);
      formik.setFieldValue('exerciseId', orderedExercise.id);
      formik.setFieldValue('programSetItems', [firstItem]);
    } else {
      formik.setFieldValue('exercise', null);
      formik.setFieldValue('exerciseId', null);
      formik.setFieldValue('programSetItems', []);
    }
  }

  return {
    formik,
    programSet,
    isLoading,
    newProgramSetId,
    onAddSetItemClick,
    onChangeExercise,
    showNewExerciseModal,
    setShowNewExerciseModal,
  };
}
